'use client';

import { RecipeSearchQuery } from '@fdc/__generated__/graphql';
import { PromotionEventModel } from '@fdc/components/ui/nz/analytics-event/analytics-models';
import { RecipeGrid } from '@fdc/components/ui/recipe/recipe-grid/recipe-grid';
import { RecipeGridBase } from '@fdc/components/ui/recipe/recipe-grid/recipe-grid-base';
import { SearchFilters } from '@fdc/components/ui/search/search-filters/search-filters';
import { SearchResultCount } from '@fdc/components/ui/search/search-result-count/search-result-count';
import { RECIPE_SORT_TYPE } from '@fdc/constants/sort-type.constants';
import { useSearchOptions } from '@fdc/hooks/useSearchOptions';
import { CorePagination } from '@woolworthslimited/core-components-react/pagination';
import FeatureRecipeTileGhost from '@woolworthslimited/core-components-react/tiles/recipe-ghost/recipe-ghost';
import clsx from 'clsx';
import { ReactElement, ReactNode } from 'react';

import styles from './recipe-results.module.scss';

type RecipeResultsProps = {
  recipeResults?: RecipeSearchQuery;
  loading: boolean;
  options: ReturnType<typeof useSearchOptions>;
  paginationPathname: string;
  children?: ReactNode;
  noResultsView?: ReactElement;
  noResultsWithFiltersView?: ReactElement;
  saveClickHandler?: (recipeId: string) => Promise<boolean>; // Optional - handling save action in recipe tile
  removeClickHandler?: (recipeId: string) => Promise<boolean>; // Optional - handling remove action in recipe tile
  onAnalyticsTrack?: (eventType: string, data: PromotionEventModel) => void;
};

export function RecipeResults({
  recipeResults,
  loading,
  options,
  paginationPathname,
  children,
  noResultsView,
  noResultsWithFiltersView,
  saveClickHandler,
  removeClickHandler,
  onAnalyticsTrack,
}: RecipeResultsProps) {
  const recipes = recipeResults?.recipes;

  const totalItems = recipes?.totalCount || 0;
  const totalPages = recipes?.totalPages || 0;
  const pageNumber = options.pageNumber; // zero based index
  const analyticsHeading = options.category || 'Recipe tiles';
  const analyticsSectionId = options.category || options.searchTerm || 'N/A';
  const analyticsOption = options.sortBy || 'LATEST';

  if (!loading && (totalItems === 0 || pageNumber >= totalPages)) {
    return noResultsView ?? null;
  }

  return (
    <div
      ref={options.containerRef}
      className={clsx(
        styles.page,
        !loading && totalItems === 0 && styles.noMinHeight
      )}
    >
      {children}
      <SearchFilters
        sortBy={options.sortBy}
        category={undefined}
        dietaries={undefined}
        staticFilterOptions={undefined}
        selectedStaticFilters={undefined}
        handleSortByChange={options.handleSortByChange}
        handleCategoryChange={undefined}
        handleStaticFilterChange={undefined}
        handleDietaryChange={undefined}
        sortByOptions={RECIPE_SORT_TYPE}
        categoryOptions={undefined}
        dietaryOptions={[]}
      />
      {!loading && totalItems > 0 && (
        <SearchResultCount
          totalItems={totalItems}
          pageNumber={options.pageNumber}
          pageSize={options.pageSize}
          itemLabel="recipes"
        />
      )}
      {loading && (
        <RecipeGridBase>
          {Array.from({ length: options.pageSize }, (_, i) => (
            <FeatureRecipeTileGhost key={i} />
          ))}
        </RecipeGridBase>
      )}
      {!loading && totalItems === 0 ? (
        (noResultsWithFiltersView ?? null)
      ) : (
        <>
          <RecipeGrid
            recipeList={recipes?.results}
            saveClickHandler={saveClickHandler}
            removeClickHandler={removeClickHandler}
            analyticsHeading={analyticsHeading}
            analyticsSectionId={analyticsSectionId}
            analyticsOption={analyticsOption}
            onAnalyticsTrack={(eventType, data) => {
              onAnalyticsTrack?.(eventType, {
                ...data,
                heading: analyticsHeading,
                option: analyticsOption,
                sectionId: analyticsSectionId,
              });
            }}
          />

          <div
            className={clsx(
              totalItems > options.pageSize && styles.paginationContainer
            )}
          >
            <CorePagination
              count={totalItems}
              pageSize={options.pageSize}
              pathname={paginationPathname}
              searchParams={options.searchParams}
              queryPageNumberName="page"
              onPageNumberClick={options.handlePageNumberClick}
            />
          </div>
        </>
      )}
    </div>
  );
}
